import React from "react";
import "./MigrationDiagram.css";

import awsLogo from "../assets/logos/aws-logo.svg";
import azureLogo from "../assets/logos/azure-logo.svg";
import gcpLogo from "../images/gcp-logo.png";
import onPremLogo from "../assets/logos/on-premises-logo.svg";

/* ──────────────── Layout constants ──────────────── */
const VW = 900, VH = 430;
const PANEL_W = 192, PANEL_H = 344, PANEL_Y = 78;
const LP_X = 8;
const RP_X = VW - LP_X - PANEL_W;            // 700

const HUB_CX = 450, HUB_CY = 250;
const HUB_W  = 158, HUB_H  = 138;
const HUB_L  = HUB_CX - HUB_W / 2;           // 371
const HUB_R  = HUB_CX + HUB_W / 2;           // 529

const L_EXIT  = LP_X + PANEL_W;              // 200
const R_ENTER = RP_X;                         // 700

// 4 items, taller rows (64px each) inside panel
const ITEM_YS = [154, 218, 282, 346];

// Hanging badge geometry  (taller, wider badges)
const BADGE_W = 88, BADGE_H = 40, BADGE_Y = 10;

/* ──────────────── SVG path helpers ──────────────── */
const lp = (y) => `M ${L_EXIT},${y} C 305,${y} 335,${HUB_CY} ${HUB_L},${HUB_CY}`;
const rp = (y) => `M ${HUB_R},${HUB_CY} C 575,${HUB_CY} 600,${y} ${R_ENTER},${y}`;

/* ──────────────── Data ──────────────── */
const DB_ITEMS = [
  { name: "PostgreSQL", color: "#4fa8d8", badge: "#1e5a7e" },
  { name: "MySQL",      color: "#f29111", badge: "#7a4800" },
  { name: "SQL Server", color: "#e87c7c", badge: "#7a1717" },
  { name: "MongoDB",    color: "#5bc34e", badge: "#1e5c17" },
];

const LEFT_BADGES = [
  { label: "AWS",     color: "#ff9900", x: 64, y: 10, w: 80  },
  { label: "Azure",   color: "#0089d6", x: 59, y: 10, w: 90  },
  { label: "On-Prem", color: "#0ea5e9", x: 45, y: 10, w: 118 },
];
const RIGHT_BADGES = [
  { label: "GCP",     color: "#34a853", x: 756, y: 10, w: 80  },
];

/* ──────────────── Database icon component ──────────────── */
function DbIcon({ name, cx, cy, color, badge }) {
  const icons = {
    PostgreSQL: (<>
      <circle r="16" fill={badge} opacity="0.3"/>
      <ellipse cx="-6" cy="-8" rx="7" ry="8" fill={color} opacity="0.7"/>
      <ellipse cx="6"  cy="-8" rx="7" ry="8" fill={color} opacity="0.7"/>
      <circle r="10" fill={badge} opacity="0.95"/>
      <text y="4" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="900" fontFamily="monospace">PG</text>
    </>),
    MySQL: (<>
      <circle r="16" fill={badge} opacity="0.3"/>
      <circle r="10" fill={badge} opacity="0.9"/>
      <text y="4" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="900" fontFamily="monospace">MY</text>
    </>),
    Oracle: (<>
      <circle r="16" fill={badge} opacity="0.2"/>
      <circle r="13" fill="none" stroke={color} strokeWidth="3.5" opacity="0.9"/>
      <circle r="5"  fill={badge} opacity="0.7"/>
    </>),
    "SQL Server": (<>
      <circle r="16" fill={badge} opacity="0.3"/>
      <rect x="-10" y="-9" width="20" height="5.5" rx="1.5" fill={color} opacity="0.8"/>
      <rect x="-10" y="-1.5" width="20" height="5.5" rx="1.5" fill={color} opacity="0.65"/>
      <rect x="-10" y="6" width="20" height="5.5" rx="1.5" fill={color} opacity="0.5"/>
    </>),
    MongoDB: (<>
      <circle r="16" fill={badge} opacity="0.3"/>
      <path d="M 0,-13 C 10,-9 11,3 6,11 C 2,15 -2,15 -6,11 C -11,3 -10,-9 0,-13 Z"
        fill={color} opacity="0.9"/>
      <line x1="0" y1="11" x2="0" y2="15" stroke={color} strokeWidth="2.5" opacity="0.7"/>
    </>),
  };
  return (
    <g transform={`translate(${cx},${cy})`}>
      {icons[name] || <circle r="16" fill={badge} opacity="0.3"/>}
    </g>
  );
}

/* ──────────────── Provider Logo component ──────────────── */
function ProviderLogo({ label }) {
  const logoSrc = {
    AWS: awsLogo,
    Azure: azureLogo,
    GCP: gcpLogo,
    "On-Prem": onPremLogo,
  }[label];

  if (!logoSrc) return null;

  return (
    <image 
      href={logoSrc} 
      x="-12" 
      y="-12" 
      width="24" 
      height="24" 
      className={label === "AWS" ? "aws-logo-theme-fix" : undefined}
    />
  );
}

/* ──────────────── Hanging badge component ──────────────── */
function HangingBadge({ x, y, w, h, color, label, floatClass, state }) {
  const cx = x + w / 2;
  const [hovered, setHovered] = React.useState(false);

  // Transition style for left badges carousel
  const carouselStyle = state !== undefined ? {
    transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
    transformOrigin: `${cx}px ${y + h / 2}px`,
    transform: state === 0
      ? "translate(0px, 0px) scale(1)"
      : state === 1
        ? "translate(52px, 4px) scale(0.78)"
        : "translate(-52px, 4px) scale(0.78)",
    opacity: state === 0 ? 1 : 0.32,
    zIndex: state === 0 ? 10 : 1,
    pointerEvents: state === 0 ? "auto" : "none",
    filter: hovered && state === 0
      ? `drop-shadow(0 0 8px ${color}) drop-shadow(0 0 18px ${color}88)`
      : "none",
  } : {
    filter: hovered
      ? `drop-shadow(0 0 8px ${color}) drop-shadow(0 0 18px ${color}88)`
      : "none",
    transition: "filter 0.3s ease",
  };

  return (
    <g style={carouselStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <g className={`md-badge-float ${floatClass}`}>
        {/* Hanging wire */}
        <line x1={cx} y1={y + h + 1} x2={cx} y2={PANEL_Y}
          stroke={color} strokeWidth="1"
          opacity={state === undefined || state === 0 ? 0.28 : 0.08}
          strokeDasharray="3 2.5"
          style={{ transition: "opacity 0.6s ease" }} />
        {/* Junction dot */}
        <circle cx={cx} cy={PANEL_Y} r="2.5" fill={color}
          opacity={state === undefined || state === 0 ? 0.45 : 0.1}
          style={{ transition: "opacity 0.6s ease" }} />
        {/* Pill body */}
        <rect x={x} y={y} width={w} height={h} rx={h / 2}
          fill="rgba(15, 23, 42, 0.98)"
          stroke={hovered && (state === 0 || state === undefined) ? color : color}
          strokeWidth={hovered && (state === 0 || state === undefined) ? "2.5" : (state === 0 || state === undefined ? "1.8" : "1")}
          style={{ backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", transition: "stroke-width 0.3s ease" }}
        />
        <rect x={x + 1.5} y={y + 1.5} width={w - 3} height={h - 3} rx={h / 2 - 1}
          fill={hovered && (state === 0 || state === undefined) ? `${color}35` : `${color}1e`}
          style={{ backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", transition: "fill 0.3s ease" }}
        />
        {/* Respective logo */}
        <g transform={`translate(${x + 22}, ${y + h / 2})`}>
          <ProviderLogo label={label} />
        </g>
        {/* Label — bigger, bolder */}
        <text x={x + 42} y={y + h / 2 + 4.5}
          fill="rgba(255,255,255,0.95)"
          fontSize="13.5" fontWeight="800" fontFamily="Outfit,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
}

/* ──────────────── Main diagram ──────────────── */
export default function MigrationDiagram() {
  const [slideIndex, setSlideIndex] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(timer);
  }, []);



  return (
    <div className="md-wrap">
      <svg viewBox={`0 0 ${VW} ${VH}`} className="md-svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="md-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="md-hub" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="6" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="md-glow-xl" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="10" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <radialGradient id="md-cg" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="rgba(168,85,247,0.35)"/>
            <stop offset="55%"  stopColor="rgba(139,92,246,0.12)"/>
            <stop offset="100%" stopColor="rgba(168,85,247,0)"/>
          </radialGradient>
          <linearGradient id="md-lg" gradientUnits="userSpaceOnUse" x1={L_EXIT} y1="0" x2={HUB_L} y2="0">
            <stop offset="0%"   stopColor="#f472b6"/>
            <stop offset="100%" stopColor="#c084fc"/>
          </linearGradient>
          <linearGradient id="md-rg" gradientUnits="userSpaceOnUse" x1={HUB_R} y1="0" x2={R_ENTER} y2="0">
            <stop offset="0%"   stopColor="#c084fc"/>
            <stop offset="100%" stopColor="#22d3ee"/>
          </linearGradient>
        </defs>

        {/* Ambient glow */}
        <ellipse cx={HUB_CX} cy={HUB_CY} rx="245" ry="165" fill="url(#md-cg)"/>

        {/* Left flow lines */}
        {ITEM_YS.map((y, i) => (
          <g key={`ll${i}`}>
            <path d={lp(y)} stroke="#f472b6" strokeWidth="12" fill="none" opacity="0.07"/>
            <path d={lp(y)} stroke="url(#md-lg)" strokeWidth="1.8" fill="none" opacity="0.28" filter="url(#md-glow)"/>
            <path d={lp(y)} stroke="#fda4d0" strokeWidth="2.8" fill="none"
              className={`md-fl md-d${i}`} filter="url(#md-glow)"/>
          </g>
        ))}

        {/* Right flow lines */}
        {ITEM_YS.map((y, i) => (
          <g key={`rl${i}`}>
            <path d={rp(y)} stroke="#22d3ee" strokeWidth="12" fill="none" opacity="0.07"/>
            <path d={rp(y)} stroke="url(#md-rg)" strokeWidth="1.8" fill="none" opacity="0.28" filter="url(#md-glow)"/>
            <path d={rp(y)} stroke="#7dd3fc" strokeWidth="2.8" fill="none"
              className={`md-fr md-d${i}`} filter="url(#md-glow)"/>
          </g>
        ))}

        {/* Hanging badges – Source (left) */}
        {[...LEFT_BADGES].map((b, i) => {
          const state = (i - slideIndex + 3) % 3;
          return { b, i, state };
        }).sort((x, y) => {
          if (x.state === 0) return 1;
          if (y.state === 0) return -1;
          return 0;
        }).map(({ b, i, state }) => (
          <HangingBadge key={`lb${i}`}
            x={b.x} y={b.y} w={b.w} h={BADGE_H}
            color={b.color} label={b.label} floatClass={`md-bd${i}`}
            state={state} />
        ))}
        {/* Hanging badges – Target (right) */}
        {RIGHT_BADGES.map((b, i) => (
          <HangingBadge key={`rb${i}`}
            x={b.x} y={b.y} w={b.w} h={BADGE_H}
            color={b.color} label={b.label} floatClass={`md-bd${i + 3}`}/>
        ))}

        {/* ─── LEFT PANEL ─── */}
        <rect x={LP_X} y={PANEL_Y} width={PANEL_W} height={PANEL_H} rx="18"
          fill="rgba(5,8,26,0.92)" stroke="rgba(99,102,241,0.25)" strokeWidth="1.5"/>
        {/* Title */}
        <text x={LP_X + PANEL_W / 2} y={PANEL_Y + 32} textAnchor="middle"
          fill="rgba(255,255,255,0.92)" fontSize="17" fontWeight="700" fontFamily="Outfit,sans-serif">
          Source Databases
        </text>

        {DB_ITEMS.map((it, i) => {
          const y = ITEM_YS[i];
          return (
            <g key={`li${i}`}>
              <rect x={LP_X + 10} y={y - 26} width={PANEL_W - 20} height={52} rx="12"
                fill="rgba(255,255,255,0.045)" stroke="rgba(255,255,255,0.075)" strokeWidth="1"/>
              <DbIcon name={it.name} cx={LP_X + 40} cy={y} color={it.color} badge={it.badge}/>
              <text x={LP_X + 68} y={y + 6} fill="rgba(255,255,255,0.9)"
                fontSize="16" fontWeight="600" fontFamily="Outfit,sans-serif">{it.name}</text>
            </g>
          );
        })}

        {/* ─── RIGHT PANEL ─── */}
        <rect x={RP_X} y={PANEL_Y} width={PANEL_W} height={PANEL_H} rx="18"
          fill="rgba(5,8,26,0.92)" stroke="rgba(34,211,238,0.2)" strokeWidth="1.5"/>
        {/* Title */}
        <text x={RP_X + PANEL_W / 2} y={PANEL_Y + 32} textAnchor="middle"
          fill="rgba(255,255,255,0.92)" fontSize="17" fontWeight="700" fontFamily="Outfit,sans-serif">
          Target Databases
        </text>

        {DB_ITEMS.map((it, i) => {
          const y = ITEM_YS[i];
          return (
            <g key={`ri${i}`}>
              <rect x={RP_X + 10} y={y - 26} width={PANEL_W - 20} height={52} rx="12"
                fill="rgba(34,211,238,0.045)" stroke="rgba(34,211,238,0.075)" strokeWidth="1"/>
              <DbIcon name={it.name} cx={RP_X + 40} cy={y} color={it.color} badge={it.badge}/>
              <text x={RP_X + 68} y={y + 6} fill="rgba(255,255,255,0.9)"
                fontSize="16" fontWeight="600" fontFamily="Outfit,sans-serif">{it.name}</text>
            </g>
          );
        })}

        {/* ─── CENTER HUB ─── */}
        <rect x={HUB_L - 16} y={HUB_CY - HUB_H / 2 - 16} width={HUB_W + 32} height={HUB_H + 32} rx="32"
          fill="none" stroke="rgba(168,85,247,0.14)" strokeWidth="3" filter="url(#md-glow-xl)"/>
        <rect x={HUB_L} y={HUB_CY - HUB_H / 2} width={HUB_W} height={HUB_H} rx="24"
          fill="rgba(6,4,22,0.97)" stroke="rgba(168,85,247,0.75)" strokeWidth="2" filter="url(#md-hub)"/>
        <rect x={HUB_L + 2} y={HUB_CY - HUB_H / 2 + 2} width={HUB_W - 4} height={HUB_H - 4} rx="22"
          fill="rgba(168,85,247,0.06)"/>

        {/* Cylinder icon */}
        <g transform={`translate(${HUB_CX},${HUB_CY - 22})`}>
          <ellipse cx="0" cy="0" rx="28" ry="10"
            fill="rgba(168,85,247,0.18)" stroke="rgba(168,85,247,0.92)" strokeWidth="1.5"/>
          <rect x="-28" y="0" width="56" height="26" fill="rgba(168,85,247,0.07)"/>
          <line x1="-28" y1="0" x2="-28" y2="26" stroke="rgba(168,85,247,0.8)" strokeWidth="1.5"/>
          <line x1="28"  y1="0" x2="28"  y2="26" stroke="rgba(168,85,247,0.8)" strokeWidth="1.5"/>
          <ellipse cx="0" cy="13" rx="28" ry="10"
            fill="none" stroke="rgba(168,85,247,0.3)" strokeWidth="1" strokeDasharray="4 3"/>
          <ellipse cx="0" cy="26" rx="28" ry="10"
            fill="rgba(168,85,247,0.14)" stroke="rgba(168,85,247,0.92)" strokeWidth="1.5"/>
        </g>
        {/* DataLift label */}
        <text x={HUB_CX} y={HUB_CY + 58} textAnchor="middle"
          fill="rgba(255,255,255,0.97)" fontSize="20" fontWeight="800"
          letterSpacing="0.5" fontFamily="Outfit,sans-serif">DataLift</text>

        {/* Assess & Validate */}
        <g transform={`translate(${HUB_CX},${HUB_CY - HUB_H / 2 - 42})`}>
          <path d="M -14,-9 L 0,-19 L 14,-9 L 14,1 C 14,11 7,16 0,18 C -7,16 -14,11 -14,1 Z"
            fill="rgba(168,85,247,0.12)" stroke="rgba(168,85,247,0.55)" strokeWidth="1.5"/>
          <path d="M -5,1 L -1,7 L 8,-5"
            fill="none" stroke="rgba(168,85,247,0.95)" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"/>
          <text y="35" textAnchor="middle" fill="rgba(255,255,255,0.58)"
            fontSize="13" fontWeight="600" fontFamily="Outfit,sans-serif">Assess &amp; Validate</text>
        </g>

        {/* Migrate & Verify */}
        <g transform={`translate(${HUB_CX},${HUB_CY + HUB_H / 2 + 20})`}>
          <path d="M -24,0 L -15,0 L -9,-12 L -3,12 L 4,-8 L 11,0 L 24,0"
            fill="none" stroke="rgba(34,211,238,0.85)" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"/>
          <text y="22" textAnchor="middle" fill="rgba(255,255,255,0.58)"
            fontSize="13" fontWeight="600" fontFamily="Outfit,sans-serif">Migrate &amp; Verify</text>
        </g>

        {/* Panel edge dots */}
        {ITEM_YS.map((y, i) => (
          <g key={`dots${i}`}>
            <circle cx={L_EXIT} cy={y} r="3.5" fill="#f472b6" opacity="0.8" filter="url(#md-glow)"/>
            <circle cx={R_ENTER} cy={y} r="3.5" fill="#22d3ee"  opacity="0.8" filter="url(#md-glow)"/>
          </g>
        ))}
        <circle cx={HUB_L} cy={HUB_CY} r="4.5" fill="#c084fc" opacity="0.95" filter="url(#md-glow)"/>
        <circle cx={HUB_R} cy={HUB_CY} r="4.5" fill="#c084fc" opacity="0.95" filter="url(#md-glow)"/>
      </svg>
    </div>
  );
}